/**
 * ManageAppModal — everything about ONE connected app, in one popup
 * (2026-08-18 UX overhaul): the account roster with per-connection removal,
 * add-another-account, access entry, folder scope (storage), share/unshare
 * (personal view), and — org view, manage-gated — disconnect-all.
 *
 * Disconnect asymmetry preserved (two-set model): personal removals are
 * per-id over the user's OWN roster (ownership server-enforced, no manage
 * gate); the org "disconnect everything" is the toolkit-level delete that
 * kills every connection for the slug — never swap them.
 */

import React, { useState } from 'react';
import PanelModal from './PanelModal';
import panelTheme from '../panelTheme';

export interface ManageAppModalProps {
  slug: string;
  displayName: string;
  view: 'personal' | 'organization';
  connections: Array<{
    id: string;
    created_at: string;
    identity?: string;
    /** Post-connect probe verdict: false = signed-in identity has no usable
     *  mailbox (2026-08-20 connect-journey batch). */
    mailboxOk?: boolean;
    /** Member-shared row: attribution by display NAME only (house rule). */
    via?: 'organization' | 'shared';
    sharedByName?: string;
  }>;
  isStorage: boolean;
  scopeLabel?: string | null;
  shared?: boolean;
  viaShared?: boolean;
  /** Org view: the sharing member's display NAME (never a uid). */
  sharedByName?: string;
  canManage: boolean;
  canShare: boolean;
  sharePending: boolean;
  shareError?: string | null;
  disconnecting: boolean;
  renderAccessChip: (slug: string, opts?: { scopeAware?: boolean }) => React.ReactNode;
  onAddAccount: () => void;
  onDisconnectConnection: (connectionId: string) => void;
  onDisconnectAll: () => void;
  onOpenFolders: () => void;
  onShare: (share: boolean) => void;
  /** W-R3 multi-workspace picker (personal-workspace context, 2026-08-25):
   *  the user's other organizations with their current shared state. When
   *  present, replaces the single share/unshare link. */
  shareTargets?: Array<{ accountId: string; label: string; shared: boolean }>;
  onShareTo?: (accountId: string, share: boolean) => void;
  onClose: () => void;
}

function addedLabel(createdAt: string): string {
  if (!createdAt) return 'account';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return 'account';
  return `added ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

export const ManageAppModal: React.FC<ManageAppModalProps> = ({
  slug,
  displayName,
  view,
  connections,
  isStorage,
  scopeLabel,
  shared,
  viaShared,
  sharedByName,
  canManage,
  canShare,
  sharePending,
  shareError,
  shareTargets,
  onShareTo,
  disconnecting,
  renderAccessChip,
  onAddAccount,
  onDisconnectConnection,
  onDisconnectAll,
  onOpenFolders,
  onShare,
  onClose,
}) => {
  // Destructive actions confirm inline — no browser confirm() dialogs.
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const canRemoveRows = view === 'personal' || canManage;

  return (
    <PanelModal
      onClose={onClose}
      width="min(520px, 94vw)"
      ariaLabel={`Manage ${displayName}`}
      testId={`manage-modal-${slug}`}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <div style={{ fontWeight: 700, color: panelTheme.ink, fontSize: '1rem' }}>
          {displayName}
        </div>
        <span style={{ fontSize: '0.72rem', color: panelTheme.grayText }}>
          {view === 'organization' ? 'organization connection' : 'my connection'}
        </span>
        <button
          onClick={onClose}
          aria-label="close"
          style={{
            marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
            color: panelTheme.grayFaint, fontSize: '1.1rem', lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Access + folders, one glance — the 📁 chip below carries the
          folder story, so the access chip stays tier-only here. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap', margin: '0.4rem 0 0.8rem' }}>
        {renderAccessChip(slug, { scopeAware: false })}
        {isStorage && (
          <button
            onClick={onOpenFolders}
            data-testid={`manage-folders-${slug}`}
            style={{
              fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.55rem',
              borderRadius: panelTheme.radiusChip, border: '1px solid transparent',
              background: panelTheme.goldTintStrong, color: panelTheme.goldInk, cursor: 'pointer',
            }}
          >
            📁 {scopeLabel || 'folders'}
          </button>
        )}
        {viaShared && (
          <span data-testid={`shared-by-${slug}`} style={{ fontSize: '0.72rem', color: panelTheme.grayText, fontWeight: 500 }}>
            {/* Attribution is by display NAME only — the uid never renders
                (house rule); no resolved name falls back to the generic note. */}
            shared by {sharedByName || 'a member'}
          </span>
        )}
      </div>

      {/* Accounts roster — one row per connection. */}
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: panelTheme.inkMuted, textTransform: 'lowercase', marginBottom: '0.35rem' }}>
        accounts · {connections.length}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.75rem' }}>
        {connections.map((c, i) => (
          <div
            key={c.id}
            data-testid={`account-row-${slug}-${i}`}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              border: `1px solid ${panelTheme.borderSoft}`, borderRadius: '8px',
              padding: '0.4rem 0.6rem', fontSize: '0.8rem', color: panelTheme.ink,
            }}
          >
            <span aria-hidden="true">👤</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              {/* WHICH account: the provider-reported identity (email first,
                  phone second) when we have it; a shared row without one is
                  attributed by the sharer's NAME (never their provider email
                  — house rule); positional label only as the last resort. */}
              <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>
                {c.identity
                  || (c.via === 'shared' ? `shared by ${c.sharedByName || 'a member'}` : `account ${i + 1}`)}
              </span>
              <span style={{ color: panelTheme.grayFaint, marginLeft: 8, fontSize: '0.72rem' }}>
                {addedLabel(c.created_at)}
              </span>
              {c.mailboxOk === false && (
                <span
                  data-testid={`account-mailbox-warning-${slug}-${i}`}
                  style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: panelTheme.danger }}
                >
                  ⚠ no usable mailbox on this sign-in — reconnect as the inbox itself
                </span>
              )}
            </span>
            {canRemoveRows && (confirmId === c.id ? (
              <>
                <span style={{ fontSize: '0.72rem', color: panelTheme.danger }}>remove this account?</span>
                <button
                  onClick={() => { setConfirmId(null); onDisconnectConnection(c.id); }}
                  disabled={disconnecting}
                  data-testid={`confirm-remove-${slug}-${i}`}
                  style={{
                    background: panelTheme.danger, color: '#fff', border: 'none',
                    borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700,
                    padding: '0.15rem 0.5rem', cursor: 'pointer',
                  }}
                >
                  yes
                </button>
                <button
                  onClick={() => setConfirmId(null)}
                  style={{
                    background: 'none', border: 'none', color: panelTheme.grayText,
                    fontSize: '0.72rem', cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  keep
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmId(c.id)}
                disabled={disconnecting}
                aria-label={`Remove account ${i + 1} of ${displayName}`}
                data-testid={`remove-account-${slug}-${i}`}
                style={{
                  background: 'none', border: `1px solid ${panelTheme.dangerBorder}`,
                  borderRadius: '6px', color: panelTheme.danger,
                  cursor: disconnecting ? 'wait' : 'pointer',
                  fontSize: '0.7rem', padding: '0.12rem 0.35rem',
                  opacity: disconnecting ? 0.5 : 1,
                }}
              >
                ✕
              </button>
            ))}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <button
          onClick={onAddAccount}
          data-testid={`manage-add-account-${slug}`}
          style={{
            fontSize: '0.78rem', fontWeight: 700, padding: '0.35rem 0.8rem',
            borderRadius: panelTheme.radiusChip, border: `1px dashed ${panelTheme.gold}`,
            background: 'none', color: panelTheme.goldInk, cursor: 'pointer',
            textTransform: 'lowercase',
          }}
        >
          + add another account
        </button>

        {view === 'personal' && canShare && (shareTargets && shareTargets.length > 0 && onShareTo ? (
          /* W-R3 (2026-08-25): connecting from YOUR OWN workspace — choose
             which of your organizations may use this connection. */
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }} data-testid={`manage-share-targets-${slug}`}>
            <span style={{ fontSize: '0.74rem', color: panelTheme.inkMuted, textTransform: 'lowercase' }}>share with:</span>
            {shareTargets.map((t) => (
              <button
                key={t.accountId}
                onClick={() => { if (!sharePending) onShareTo(t.accountId, !t.shared); }}
                disabled={sharePending}
                data-testid={`manage-share-target-${slug}-${t.accountId}`}
                style={{
                  fontSize: '0.74rem', fontWeight: 600, padding: '0.22rem 0.65rem',
                  borderRadius: panelTheme.radiusChip,
                  border: `1px solid ${t.shared ? panelTheme.successDeep : panelTheme.borderSoft}`,
                  background: t.shared ? panelTheme.successBg : 'none',
                  color: t.shared ? panelTheme.successDeep : panelTheme.inkMuted,
                  cursor: sharePending ? 'wait' : 'pointer', textTransform: 'lowercase',
                }}
              >
                {t.shared ? '✓ ' : ''}{t.label}
              </button>
            ))}
          </span>
        ) : (
          <button
            onClick={() => { if (!sharePending) onShare(!shared); }}
            disabled={sharePending}
            data-testid={`manage-share-${slug}`}
            style={{
              fontSize: '0.78rem', fontWeight: 600, padding: 0,
              background: 'none', border: 'none', color: panelTheme.goldInk,
              textDecoration: 'underline',
              cursor: sharePending ? 'wait' : 'pointer',
              opacity: sharePending ? 0.6 : 1, textTransform: 'lowercase',
            }}
          >
            {sharePending ? '…' : shared ? 'unshare' : 'share with organization'}
          </button>
        ))}

        {view === 'organization' && canManage && (confirmAll ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ fontSize: '0.74rem', color: panelTheme.danger }}>
              disconnect every {displayName} account?
            </span>
            <button
              onClick={() => { setConfirmAll(false); onDisconnectAll(); }}
              disabled={disconnecting}
              data-testid={`confirm-disconnect-all-${slug}`}
              style={{
                background: panelTheme.danger, color: '#fff', border: 'none',
                borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700,
                padding: '0.15rem 0.5rem', cursor: 'pointer',
              }}
            >
              yes, disconnect
            </button>
            <button
              onClick={() => setConfirmAll(false)}
              style={{
                background: 'none', border: 'none', color: panelTheme.grayText,
                fontSize: '0.72rem', cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              keep
            </button>
          </span>
        ) : (
          <button
            onClick={() => setConfirmAll(true)}
            disabled={disconnecting}
            data-testid={`manage-disconnect-all-${slug}`}
            style={{
              marginLeft: 'auto', fontSize: '0.76rem', fontWeight: 600,
              background: 'none', border: 'none', color: panelTheme.danger,
              textDecoration: 'underline', cursor: 'pointer', textTransform: 'lowercase',
            }}
          >
            disconnect all
          </button>
        ))}
      </div>

      {shareError && (
        <div style={{ fontSize: '0.74rem', color: panelTheme.danger, marginTop: '0.5rem' }}>
          {shareError}
        </div>
      )}
    </PanelModal>
  );
};

export default ManageAppModal;
